/**
 * CronosMonitor Page
 *
 * Monitor Cronos POS Chain staking: delegations, rewards, APY, and validators.
 * Includes Lotto tab for no-loss lottery participation.
 * Similar structure to FluxNode Monitor and Hedera Monitor.
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../components/Icons';
import { useToast } from '../components/Toast';
import { useCronosStaking, useValidateCronosAddress } from '../hooks/useCronosStaking';
import { useCronosWallets } from '../hooks/useMonitorWallets';
import { CronosLottoPromoPopup } from '../components/cronos/CronosLottoPromoPopup';
import { CronosHealthMetrics } from '../components/cronos/CronosHealthMetrics';
import { CroSupplyBreakdown } from '../components/cronos/CroSupplyBreakdown';
import { CronosLottoTab } from '../components/cronos/CronosLottoTab';
import {
  NetworkMonitorPageHeader,
  NetworkAddressInputForm,
  MonitorTabs,
} from '../components/monitor';
import type { CronosWalletData, CronosDelegationInfo, CronosUnbondingInfo } from '../types/cronos.types';

// ============================================
// CONSTANTS
// ============================================

const CRONOS_COLORS = {
  primary: '#1199FA', // Cronos light blue (used for icons + accents)
  secondary: '#1199FA', // Cronos light blue
  accent: '#00D2FF', // Cronos cyan
  gradient: 'linear-gradient(135deg, #002D74, #1199FA)',
  lightGradient: 'linear-gradient(135deg, rgba(0, 45, 116, 0.15), rgba(17, 153, 250, 0.1))',
  lottoGradient: 'linear-gradient(135deg, #667eea, #764ba2)',
  goldGradient: 'linear-gradient(135deg, #f5af19, #f12711)',
};

type TabType = 'staking' | 'lotto';

// ============================================
// STYLES
// ============================================

const s = {
  page: {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    gap: '20px',
    flexWrap: 'wrap' as const,
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  titleText: {
    fontSize: '24px',
    fontWeight: 700,
    margin: 0,
  },
  subtitle: {
    fontSize: '13px',
    color: 'var(--muted)',
    marginTop: '4px',
  },
  // Tab styles
  tabContainer: {
    display: 'flex',
    gap: '4px',
    padding: '4px',
    background: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '14px',
    width: 'fit-content',
  },
  tab: (isActive: boolean, isLotto: boolean) => ({
    padding: '12px 24px',
    borderRadius: '10px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.3s ease',
    background: isActive
      ? isLotto
        ? CRONOS_COLORS.lottoGradient
        : CRONOS_COLORS.gradient
      : 'transparent',
    color: isActive ? 'white' : 'var(--muted)',
    transform: isActive ? 'scale(1.02)' : 'scale(1)',
    boxShadow: isActive ? '0 4px 15px rgba(0, 0, 0, 0.3)' : 'none',
  }),
  card: {
    background: 'rgba(0, 0, 0, 0.25)',
    backdropFilter: 'blur(20px)',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    padding: '20px',
    marginBottom: '20px',
  },
  cardTitle: {
    fontSize: '15px',
    fontWeight: 600,
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: {
    background: 'rgba(0, 45, 116, 0.15)',
    border: '1px solid rgba(17, 153, 250, 0.2)',
    borderRadius: '12px',
    padding: '16px',
    textAlign: 'center' as const,
  },
  statValue: {
    fontSize: '24px',
    fontWeight: 700,
    color: CRONOS_COLORS.secondary,
  },
  statLabel: {
    fontSize: '11px',
    color: 'var(--muted)',
    marginTop: '4px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    background: 'rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: '10px',
    color: 'var(--text)',
    fontSize: '13px',
    fontFamily: 'monospace',
    outline: 'none',
  },
  button: {
    padding: '12px 20px',
    background: CRONOS_COLORS.gradient,
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  walletItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 14px',
    background: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '10px',
    marginBottom: '8px',
    border: '1px solid rgba(255, 255, 255, 0.06)',
  },
  delegationCard: {
    background: 'rgba(0, 45, 116, 0.1)',
    borderLeft: `4px solid ${CRONOS_COLORS.secondary}`,
    borderRadius: '10px',
    padding: '16px',
    marginBottom: '12px',
  },
  unbondingCard: {
    background: 'rgba(245, 158, 11, 0.1)',
    borderLeft: '4px solid #f59e0b',
    borderRadius: '10px',
    padding: '16px',
    marginBottom: '12px',
  },
  progressBar: {
    width: '100%',
    height: '6px',
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '3px',
    overflow: 'hidden',
    marginTop: '8px',
  },
  progressFill: {
    height: '100%',
    background: CRONOS_COLORS.gradient,
    borderRadius: '3px',
    transition: 'width 0.3s',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '40px 20px',
    color: 'var(--muted)',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: 600,
  },
  // Lotto specific styles
  lottoHero: {
    background: `linear-gradient(135deg, rgba(102, 126, 234, 0.2), rgba(118, 75, 162, 0.2))`,
    border: '1px solid rgba(102, 126, 234, 0.3)',
    borderRadius: '20px',
    padding: '32px',
    marginBottom: '24px',
    textAlign: 'center' as const,
    position: 'relative' as const,
    overflow: 'hidden',
  },
  lottoJackpot: {
    fontSize: '48px',
    fontWeight: 800,
    background: 'linear-gradient(135deg, #FFD700, #FFA500)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: '8px',
    textShadow: '0 0 40px rgba(255, 215, 0, 0.3)',
  },
  ticketNumber: (isMatched: boolean) => ({
    width: '42px',
    height: '42px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '16px',
    background: isMatched
      ? 'linear-gradient(135deg, #10b981, #059669)'
      : 'rgba(255, 255, 255, 0.1)',
    border: isMatched
      ? '2px solid #10b981'
      : '1px solid rgba(255, 255, 255, 0.2)',
    color: isMatched ? 'white' : 'var(--text)',
    transition: 'all 0.3s ease',
  }),
  tierCard: (color: string) => ({
    background: `${color}15`,
    border: `1px solid ${color}40`,
    borderRadius: '12px',
    padding: '16px',
    textAlign: 'center' as const,
  }),
};

/**
 * Network Stats Banner
 */
function NetworkStatsBanner({ networkStats, croPrice }: {
  networkStats: ReturnType<typeof useCronosStaking>['networkStats'];
  croPrice: number;
}) {
  const { t } = useTranslation();

  if (!networkStats) return null;

  return (
    <div style={{
      ...s.card,
      background: `linear-gradient(135deg, rgba(0, 45, 116, 0.2), rgba(17, 153, 250, 0.1))`,
      border: '1px solid rgba(17, 153, 250, 0.2)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>
            {t('cronos.network.title', 'Cronos POS Network')}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
            <span style={{ fontSize: '28px', fontWeight: 700, color: CRONOS_COLORS.secondary }}>
              {networkStats.averageApy.toFixed(2)}%
            </span>
            <span style={{ fontSize: '13px', color: 'var(--muted)' }}>APY</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 600 }}>
              {networkStats.activeValidators}
            </div>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>
              {t('cronos.network.validators', 'Validators')}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 600 }}>
              {(networkStats.totalBonded / 1e9).toFixed(2)}B
            </div>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>
              {t('cronos.network.totalStaked', 'CRO Staked')}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 600, color: '#10b981' }}>
              ${croPrice.toFixed(4)}
            </div>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>
              {t('cronos.network.price', 'CRO Price')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


/**
 * Wallet List Item
 */
function WalletListItem({ address, onRemove }: { address: string; onRemove: () => void }) {
  return (
    <div style={s.walletItem}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          background: CRONOS_COLORS.gradient,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Icons.Wallet size={16} color="white" />
        </div>
        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {address.slice(0, 12)}...{address.slice(-8)}
        </span>
      </div>
      <button
        onClick={onRemove}
        style={{
          background: 'rgba(239, 68, 68, 0.15)',
          color: '#ef4444',
          border: 'none',
          borderRadius: '6px',
          padding: '6px 10px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          fontSize: '11px',
        }}
      >
        <Icons.Trash2 size={12} />
      </button>
    </div>
  );
}

/**
 * Stats Overview Cards
 */
function StatsOverview({ aggregatedData, croPrice }: {
  aggregatedData: ReturnType<typeof useCronosStaking>['aggregatedData'];
  croPrice: number;
}) {
  const { t } = useTranslation();

  if (!aggregatedData) return null;

  const stats = [
    {
      label: t('cronos.stats.totalValue', 'Total Value'),
      value: `${aggregatedData.totalValue.toLocaleString(undefined, { maximumFractionDigits: 2 })} CRO`,
      subvalue: `$${aggregatedData.totalValueUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
      color: CRONOS_COLORS.secondary,
    },
    {
      label: t('cronos.stats.staked', 'Staked'),
      value: `${aggregatedData.totalStaked.toLocaleString(undefined, { maximumFractionDigits: 2 })} CRO`,
      subvalue: `$${(aggregatedData.totalStaked * croPrice).toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
      color: '#8b5cf6',
    },
    {
      label: t('cronos.stats.rewards', 'Pending Rewards'),
      value: `${aggregatedData.totalRewards.toLocaleString(undefined, { maximumFractionDigits: 4 })} CRO`,
      subvalue: `$${(aggregatedData.totalRewards * croPrice).toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
      color: '#10b981',
    },
    {
      label: t('cronos.stats.available', 'Available'),
      value: `${aggregatedData.totalAvailable.toLocaleString(undefined, { maximumFractionDigits: 2 })} CRO`,
      subvalue: `$${(aggregatedData.totalAvailable * croPrice).toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
      color: '#f59e0b',
    },
    {
      label: t('cronos.stats.apy', 'Weighted APY'),
      value: `${aggregatedData.weightedApy.toFixed(2)}%`,
      subvalue: t('cronos.stats.perYear', 'per year'),
      color: '#ec4899',
    },
    {
      label: t('cronos.stats.validators', 'Validators'),
      value: aggregatedData.uniqueValidators.toString(),
      subvalue: t('cronos.stats.delegatedTo', 'delegated to'),
      color: '#06b6d4',
    },
  ];

  return (
    <div style={s.statsGrid}>
      {stats.map((stat, idx) => (
        <div key={idx} style={{ ...s.statCard, borderColor: `${stat.color}30` }}>
          <div style={{ ...s.statValue, color: stat.color }}>{stat.value}</div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>{stat.subvalue}</div>
          <div style={s.statLabel}>{stat.label}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * Delegation Card Component
 */
function DelegationCard({ delegation }: { delegation: CronosDelegationInfo; croPrice: number }) {
  const { t } = useTranslation();

  return (
    <div style={s.delegationCard}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div>
          <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '2px' }}>
            {delegation.validatorMoniker}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', fontFamily: 'monospace' }}>
            {delegation.validatorAddress.slice(0, 20)}...
          </div>
        </div>
        <span style={{
          ...s.badge,
          background: 'rgba(17, 153, 250, 0.15)',
          color: CRONOS_COLORS.secondary,
        }}>
          {delegation.validatorCommission.toFixed(1)}% commission
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
            {t('cronos.delegation.staked', 'Staked')}
          </div>
          <div style={{ fontSize: '16px', fontWeight: 600 }}>
            {delegation.delegatedAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })} CRO
          </div>
          <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
            ${delegation.delegatedAmountUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
            {t('cronos.delegation.rewards', 'Pending Rewards')}
          </div>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#10b981' }}>
            {delegation.pendingRewards.toLocaleString(undefined, { maximumFractionDigits: 6 })} CRO
          </div>
          <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
            ${delegation.pendingRewardsUsd.toLocaleString(undefined, { maximumFractionDigits: 4 })}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Unbonding Card Component
 */
function UnbondingCard({ unbonding }: { unbonding: CronosUnbondingInfo }) {
  const { t } = useTranslation();
  const progress = Math.max(0, Math.min(100, ((28 - unbonding.remainingDays) / 28) * 100));

  return (
    <div style={s.unbondingCard}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
        <div>
          <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '2px' }}>
            {unbonding.validatorMoniker}
          </div>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#f59e0b' }}>
            {unbonding.amount.toLocaleString(undefined, { maximumFractionDigits: 2 })} CRO
          </div>
        </div>
        <span style={{
          ...s.badge,
          background: 'rgba(245, 158, 11, 0.15)',
          color: '#f59e0b',
        }}>
          <Icons.Clock size={12} />
          {unbonding.remainingDays} {t('cronos.unbonding.daysLeft', 'days left')}
        </span>
      </div>

      <div style={s.progressBar}>
        <div style={{ ...s.progressFill, width: `${progress}%`, background: 'linear-gradient(90deg, #f59e0b, #fbbf24)' }} />
      </div>

      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '6px' }}>
        {t('cronos.unbonding.availableOn', 'Available on')}: {unbonding.completionTime.toLocaleDateString()}
      </div>
    </div>
  );
}

/**
 * Wallet Details Section
 */
function WalletDetails({ wallet, croPrice }: { wallet: CronosWalletData; croPrice: number }) {
  const { t } = useTranslation();
  const [showDelegations, setShowDelegations] = useState(true);

  return (
    <div style={{ ...s.card, marginBottom: '16px' }}>
      {/* Wallet Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: CRONOS_COLORS.gradient,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Icons.Wallet size={20} color="white" />
          </div>
          <div>
            <div style={{ fontFamily: 'monospace', fontSize: '13px', marginBottom: '2px' }}>
              {wallet.address.slice(0, 16)}...{wallet.address.slice(-8)}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
              {wallet.validatorCount} {t('cronos.wallet.validators', 'validators')} • {wallet.estimatedApy.toFixed(2)}% APY
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '18px', fontWeight: 700, color: CRONOS_COLORS.secondary }}>
            {wallet.totalValue.toLocaleString(undefined, { maximumFractionDigits: 2 })} CRO
          </div>
          <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
            ${wallet.totalValueUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>
        <div style={{ padding: '10px', background: 'rgba(0, 0, 0, 0.2)', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>{wallet.availableBalance.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
          <div style={{ fontSize: '10px', color: 'var(--muted)' }}>{t('cronos.wallet.available', 'Available')}</div>
        </div>
        <div style={{ padding: '10px', background: 'rgba(0, 0, 0, 0.2)', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#8b5cf6' }}>{wallet.totalStaked.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
          <div style={{ fontSize: '10px', color: 'var(--muted)' }}>{t('cronos.wallet.staked', 'Staked')}</div>
        </div>
        <div style={{ padding: '10px', background: 'rgba(0, 0, 0, 0.2)', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#10b981' }}>{wallet.totalRewards.toLocaleString(undefined, { maximumFractionDigits: 4 })}</div>
          <div style={{ fontSize: '10px', color: 'var(--muted)' }}>{t('cronos.wallet.rewards', 'Rewards')}</div>
        </div>
        <div style={{ padding: '10px', background: 'rgba(0, 0, 0, 0.2)', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#f59e0b' }}>{wallet.totalUnbonding.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
          <div style={{ fontSize: '10px', color: 'var(--muted)' }}>{t('cronos.wallet.unbonding', 'Unbonding')}</div>
        </div>
      </div>

      {/* Delegations Toggle */}
      {wallet.delegations.length > 0 && (
        <div>
          <button
            onClick={() => setShowDelegations(!showDelegations)}
            style={{
              width: '100%',
              padding: '10px',
              background: 'rgba(0, 0, 0, 0.2)',
              border: 'none',
              borderRadius: '8px',
              color: 'var(--text)',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: showDelegations ? '12px' : 0,
            }}
          >
            <span style={{ fontSize: '13px', fontWeight: 600 }}>
              {t('cronos.wallet.delegations', 'Delegations')} ({wallet.delegations.length})
            </span>
            <Icons.ChevronDown
              size={18}
              style={{
                transform: showDelegations ? 'rotate(180deg)' : 'rotate(0)',
                transition: 'transform 0.2s',
              }}
            />
          </button>

          {showDelegations && (
            <div>
              {wallet.delegations.map((d, idx) => (
                <DelegationCard key={idx} delegation={d} croPrice={croPrice} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Unbondings */}
      {wallet.unbondings.length > 0 && (
        <div style={{ marginTop: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '12px', color: '#f59e0b' }}>
            <Icons.Clock size={14} style={{ marginRight: '6px' }} />
            {t('cronos.wallet.unbonding', 'Unbonding')} ({wallet.unbondings.length})
          </div>
          {wallet.unbondings.map((u, idx) => (
            <UnbondingCard key={idx} unbonding={u} />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// MAIN PAGE
// ============================================

export default function CronosMonitor() {
  const { t } = useTranslation();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<TabType>('staking');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newWalletInput, setNewWalletInput] = useState('');
  const { isValid: isNewWalletValid } = useValidateCronosAddress(newWalletInput);

  // Wallet management with Supabase sync
  const {
    wallets,
    setWallets,
  } = useCronosWallets();

  // Fetch staking data
  const {
    croPrice,
    networkStats,
    wallets: walletsData,
    aggregatedData,
    isLoading,
    error,
    refetch,
  } = useCronosStaking(wallets);

  // Add wallet
  const handleAddWallet = useCallback((address: string) => {
    if (wallets.includes(address)) {
      toast(t('cronos.errors.alreadyAdded', 'Wallet already added'), 'warning');
      return;
    }
    setWallets([...wallets, address]);
    toast(t('cronos.success.walletAdded', 'Wallet added'), 'success');
  }, [wallets, setWallets, toast, t]);

  // Submit handler for the shared NetworkAddressInputForm primitive.
  const handleSubmitNewWallet = useCallback(() => {
    const trimmed = newWalletInput.trim().toLowerCase();
    if (!isNewWalletValid || !trimmed) return;
    handleAddWallet(trimmed);
    setNewWalletInput('');
    setShowAddForm(false);
  }, [newWalletInput, isNewWalletValid, handleAddWallet]);

  // Remove wallet
  const handleRemoveWallet = useCallback((address: string) => {
    setWallets(wallets.filter(w => w !== address));
    toast(t('cronos.success.walletRemoved', 'Wallet removed'), 'info');
  }, [wallets, setWallets, toast, t]);

  return (
    <div style={s.page}>
      <NetworkMonitorPageHeader
        icon={
          activeTab === 'lotto' ? (
            <span style={{ fontSize: '32px' }} aria-hidden>🎰</span>
          ) : (
            <Icons.Database size={36} color={CRONOS_COLORS.primary} />
          )
        }
        title={t('cronos.title', 'Cronos Staking')}
        subtitle={t('cronos.subtitle', 'Monitor your CRO staking, rewards, and validators')}
        primaryAction={
          activeTab === 'staking'
            ? showAddForm
              ? {
                  label: t('common.cancel', 'Cancel'),
                  icon: <Icons.X size={16} />,
                  onClick: () => setShowAddForm(false),
                  variant: 'secondary' as const,
                }
              : {
                  label: t('cronos.addWallet', 'Add Wallet'),
                  icon: <Icons.Plus size={16} />,
                  onClick: () => setShowAddForm(true),
                }
            : undefined
        }
        refreshAction={
          activeTab === 'staking'
            ? {
                label: t('common.refresh', 'Refresh'),
                icon: <Icons.RefreshCw size={16} />,
                onClick: () => refetch(),
              }
            : undefined
        }
        colors={CRONOS_COLORS}
      />

      <MonitorTabs
        tabs={[
          {
            id: 'staking' as const,
            label: t('cronos.tabs.staking', 'Staking'),
            icon: <Icons.Database size={16} />,
          },
          {
            id: 'lotto' as const,
            label: t('cronos.tabs.delegationLotto', 'Délégation + Lotto'),
            icon: <span style={{ fontSize: '14px' }} aria-hidden>🎰</span>,
          },
        ]}
        activeTab={activeTab}
        onChange={setActiveTab}
        colors={CRONOS_COLORS}
        ariaLabel={t('cronos.tabs.ariaLabel', 'Cronos Monitor sections')}
      />

      {showAddForm && activeTab === 'staking' && (
        <NetworkAddressInputForm
          value={newWalletInput}
          onChange={setNewWalletInput}
          onSubmit={handleSubmitNewWallet}
          submitLabel={t('common.add', 'Add')}
          placeholder={t('cronos.wallet.placeholder', 'cro1...')}
          isValid={newWalletInput.trim() ? isNewWalletValid : null}
          colors={CRONOS_COLORS}
        />
      )}

      {/* Tab Content */}
      {activeTab === 'staking' ? (
        <>
          {/* Network Stats Banner */}
          <NetworkStatsBanner networkStats={networkStats} croPrice={croPrice} />

          {/* V2.K.1 — CRO supply breakdown (circulating / locked /
              remaining-to-mint + CIP-006 dilution note + known
              addresses). Fed by CoinGecko, refreshes every 5 min. */}
          <CroSupplyBreakdown />

          {/* Blockchain Health Metrics - Collapsible */}
          <CronosHealthMetrics />

          {/* Wallet Management */}
          <div style={s.card}>
            <h2 style={s.cardTitle}>
              <Icons.Wallet size={18} />
              {t('cronos.wallets.title', 'Wallets')}
              {wallets.length > 0 && (
                <span style={{
                  background: CRONOS_COLORS.gradient,
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '11px',
                }}>
                  {wallets.length}
                </span>
              )}
            </h2>

            {wallets.length > 0 ? (
              <div>
                {wallets.map(address => (
                  <WalletListItem
                    key={address}
                    address={address}
                    onRemove={() => handleRemoveWallet(address)}
                  />
                ))}
              </div>
            ) : (
              <div style={s.emptyState}>
                <Icons.Wallet size={40} style={{ opacity: 0.3, marginBottom: '12px' }} />
                <p style={{ margin: 0, fontSize: '13px' }}>
                  {t('cronos.wallets.empty', 'Add a Cronos wallet address (cro1...) to start tracking')}
                </p>
              </div>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div style={{
              ...s.card,
              background: 'rgba(239, 68, 68, 0.15)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#ef4444' }}>
                <Icons.AlertTriangle size={20} />
                <span>{t('cronos.errors.loadFailed', 'Failed to load data')}: {String(error)}</span>
              </div>
            </div>
          )}

          {/* Loading State */}
          {isLoading && wallets.length > 0 && (
            <div style={{ ...s.card, textAlign: 'center', padding: '40px' }}>
              <Icons.RefreshCw size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: '12px', color: CRONOS_COLORS.secondary }} />
              <p style={{ color: 'var(--muted)', margin: 0 }}>{t('common.loading', 'Loading...')}</p>
            </div>
          )}

          {/* Stats Overview */}
          {!isLoading && aggregatedData && (
            <StatsOverview aggregatedData={aggregatedData} croPrice={croPrice} />
          )}

          {/* Lotto Bonus Widget - Promotion on Staking tab */}
          <div style={{
            ...s.card,
            background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.08), rgba(255, 165, 0, 0.04))',
            border: '1px solid rgba(255, 215, 0, 0.2)',
            marginTop: '16px',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '16px',
            }}>
              {/* Left: Lotto bonus info */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <img
                  src="/loaded-lion.svg"
                  alt="Lotto"
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '10px',
                    boxShadow: '0 2px 10px rgba(255, 215, 0, 0.2)',
                  }}
                />
                <div>
                  <div style={{
                    fontSize: '13px',
                    color: 'var(--muted)',
                    marginBottom: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}>
                    <span>🎰</span>
                    {t('cronos.stakingLotto.title', 'Bonus Lotto Edouard.ai')}
                  </div>
                  <div style={{
                    fontSize: '28px',
                    fontWeight: 800,
                    color: aggregatedData && aggregatedData.totalStaked >= 10000
                      ? '#FFD700'
                      : 'var(--muted)',
                  }}>
                    {aggregatedData && aggregatedData.totalStaked >= 10000
                      ? `+${((aggregatedData.totalStaked * 0.0744 / 365) * 30 * 0.3).toFixed(0)} CRO`
                      : '0 CRO'
                    }
                  </div>
                  {(!aggregatedData || aggregatedData.totalStaked < 10000) && (
                    <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
                      {t('cronos.stakingLotto.notEligible', 'Déléguez min. 10,000 CRO à notre validateur')}
                    </div>
                  )}
                </div>
              </div>

              {/* Center: CRO Coins treasure */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: '1 1 auto',
                minWidth: '100px',
              }}>
                <svg width="80" height="60" viewBox="0 0 80 60" style={{ filter: 'drop-shadow(0 2px 4px rgba(255, 215, 0, 0.3))' }}>
                  {/* Coin 1 - Back left */}
                  <g>
                    <ellipse cx="20" cy="40" rx="14" ry="4" fill="#B8860B" />
                    <ellipse cx="20" cy="36" rx="14" ry="4" fill="#DAA520" />
                    <rect x="6" y="36" width="28" height="4" fill="#CD853F" />
                    <ellipse cx="20" cy="36" rx="11" ry="3" fill="#FFD700" />
                    <circle cx="20" cy="36" r="5" fill="#DAA520" />
                    <animate attributeName="opacity" values="1;0.9;1" dur="2s" repeatCount="indefinite" />
                  </g>
                  {/* Coin 2 - Front center */}
                  <g>
                    <ellipse cx="40" cy="45" rx="14" ry="4" fill="#B8860B" />
                    <ellipse cx="40" cy="41" rx="14" ry="4" fill="#DAA520" />
                    <rect x="26" y="41" width="28" height="4" fill="#CD853F" />
                    <ellipse cx="40" cy="41" rx="11" ry="3" fill="#FFD700" />
                    <circle cx="40" cy="41" r="5" fill="#DAA520" />
                    <animate attributeName="opacity" values="0.9;1;0.9" dur="2.2s" repeatCount="indefinite" />
                  </g>
                  {/* Coin 3 - Back right */}
                  <g>
                    <ellipse cx="60" cy="38" rx="14" ry="4" fill="#B8860B" />
                    <ellipse cx="60" cy="34" rx="14" ry="4" fill="#DAA520" />
                    <rect x="46" y="34" width="28" height="4" fill="#CD853F" />
                    <ellipse cx="60" cy="34" rx="11" ry="3" fill="#FFD700" />
                    <circle cx="60" cy="34" r="5" fill="#DAA520" />
                    <animate attributeName="opacity" values="1;0.95;1" dur="1.8s" repeatCount="indefinite" />
                  </g>
                  {/* Sparkles */}
                  <circle cx="10" cy="15" r="2" fill="#FFD700">
                    <animate attributeName="opacity" values="0;1;0" dur="1.5s" repeatCount="indefinite" />
                  </circle>
                  <circle cx="70" cy="20" r="1.5" fill="#FFA500">
                    <animate attributeName="opacity" values="0;1;0" dur="2s" repeatCount="indefinite" />
                  </circle>
                  <circle cx="40" cy="10" r="2" fill="#FFD700">
                    <animate attributeName="opacity" values="0;1;0" dur="1.8s" repeatCount="indefinite" />
                  </circle>
                </svg>
              </div>

              {/* Right: Stats + CTA */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                {/* Mini stats */}
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{
                    textAlign: 'center',
                    padding: '8px 12px',
                    background: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '8px',
                  }}>
                    <div style={{ fontSize: '16px', fontWeight: 700, color: '#10b981' }}>50</div>
                    <div style={{ fontSize: '9px', color: 'var(--muted)' }}>{t('cronos.stakingLotto.winners', 'gagnants/jour')}</div>
                  </div>
                  <div style={{
                    textAlign: 'center',
                    padding: '8px 12px',
                    background: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '8px',
                  }}>
                    <div style={{ fontSize: '16px', fontWeight: 700, color: '#FFD700' }}>30%</div>
                    <div style={{ fontSize: '9px', color: 'var(--muted)' }}>{t('cronos.stakingLotto.bonus', 'bonus gains')}</div>
                  </div>
                </div>

                {/* CTA Button */}
                <button
                  onClick={() => setActiveTab('lotto')}
                  style={{
                    padding: '12px 20px',
                    background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#1a1a2e',
                    fontSize: '13px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(255, 215, 0, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {t('cronos.stakingLotto.cta', 'Voir le Lotto')}
                  <Icons.ArrowRight size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* Wallet Details */}
          {!isLoading && walletsData.length > 0 && (
            <div>
              <h3 style={{ ...s.cardTitle, marginBottom: '16px' }}>
                <Icons.PieChart size={18} />
                {t('cronos.details.title', 'Wallet Details')}
              </h3>
              {walletsData.map(wallet => (
                <WalletDetails key={wallet.address} wallet={wallet} croPrice={croPrice} />
              ))}
            </div>
          )}
        </>
      ) : (
        <CronosLottoTab
          wallets={wallets}
          totalStaked={aggregatedData?.totalStaked || 0}
          croPrice={croPrice}
        />
      )}

      {/* CSS for animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.3; }
          50% { transform: translateY(-20px) rotate(180deg); opacity: 0.8; }
        }
      `}</style>

      {/* Lotto Promo Popup - Only show on staking tab */}
      {activeTab === 'staking' && (
        <CronosLottoPromoPopup
          wallets={wallets}
          totalStaked={aggregatedData?.totalStaked || 0}
          croPrice={croPrice}
        />
      )}
    </div>
  );
}
